import {
  action,
  comparer,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
  type IReactionDisposer,
} from 'mobx';
import { hydrateStore, isHydrated, makePersistable } from 'mobx-persist-store';

import { widgetsConfigStore } from '@/features/widgets/widgets-config.store';
import {
  WidgetEntrySchema,
  widgetEntryKey,
  type RoutineIslandEntry,
  type ScheduleFilterMode,
  type SingletonEntry,
  type SingletonWidgetId,
  type WidgetEntry,
} from '@/features/widgets/widgets.types';

export const HOME_BUILT_IN_SECTION_IDS = [
  'upcoming-meetings',
] as const satisfies readonly SingletonWidgetId[];

const HOME_BUILT_IN_SECTION_SET = new Set<SingletonWidgetId>(
  HOME_BUILT_IN_SECTION_IDS
);

export const HOME_SECTION_IDS = [
  'integration-suggestions',
  'upcoming-meetings',
  'pinned-routines',
  'feed',
] as const;

export type HomeSectionId = (typeof HOME_SECTION_IDS)[number];

// Match the section order persisted before suggestions moved to the top.
const OLD_DEFAULT_HOME_SECTION_IDS = [
  'upcoming-meetings',
  'pinned-routines',
  'integration-suggestions',
  'feed',
] as const satisfies readonly HomeSectionId[];

type RoutineList = {
  hasLoadedFromServer: boolean;
  getByReferenceId(referenceId: number): object | undefined;
};

const isHomeSectionId = (value: unknown): value is HomeSectionId =>
  HOME_SECTION_IDS.some((id) => id === value);

// Keeps the user's order for known sections and slots each unseen section in after its default predecessor
const reconcileSectionOrder = (raw: unknown): HomeSectionId[] => {
  const known = Array.isArray(raw) ? raw.filter(isHomeSectionId) : [];
  const order = [...new Set(known)];
  HOME_SECTION_IDS.forEach((id, defaultIndex) => {
    if (order.includes(id)) return;
    const previous = HOME_SECTION_IDS[defaultIndex - 1];
    order.splice(previous ? order.indexOf(previous) + 1 : 0, 0, id);
  });
  return order;
};

// localStorage key is already written on users' machines and must not change
const HOME_PREFERENCES_STORAGE_KEY = 'unifiedHomeConfigStore';

const createDefaultEntries = (): WidgetEntry[] =>
  HOME_BUILT_IN_SECTION_IDS.map((type, order): SingletonEntry => ({
    kind: 'singleton',
    type,
    visible: true,
    collapsed: false,
    order,
  }));

const parsePersistedEntries = (raw: unknown): WidgetEntry[] => {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    const parsed = WidgetEntrySchema.safeParse(entry);
    return parsed.success ? [parsed.data] : [];
  });
};

const isHomeSingleton = (entry: WidgetEntry): entry is SingletonEntry =>
  entry.kind === 'singleton' && HOME_BUILT_IN_SECTION_SET.has(entry.type);

const isPinnedRoutine = (entry: WidgetEntry): entry is RoutineIslandEntry =>
  entry.kind === 'pinned-routine';

export class HomePreferencesStore {
  entries: WidgetEntry[] = createDefaultEntries();
  scheduleFilterMode: ScheduleFilterMode = 'all';
  legacyPinsImported = false;
  suggestionsMovedToTop = false;
  sectionOrder: HomeSectionId[] = [...HOME_SECTION_IDS];
  hiddenSections: HomeSectionId[] = [];
  collapsedSections: HomeSectionId[] = [];

  constructor() {
    makeObservable(this, {
      entries: observable,
      scheduleFilterMode: observable,
      legacyPinsImported: observable,
      suggestionsMovedToTop: observable,
      sectionOrder: observable,
      hiddenSections: observable,
      collapsedSections: observable,
      setVisible: action.bound,
      setSectionVisible: action.bound,
      moveSection: action.bound,
      setCollapsed: action.bound,
      setScheduleFilterMode: action.bound,
      addPinnedRoutine: action.bound,
      removeInstance: action.bound,
      importLegacyPins: action.bound,
      reset: action.bound,
      orderedVisibleEntries: computed,
    });

    makePersistable(this, {
      name: HOME_PREFERENCES_STORAGE_KEY,
      properties: [
        'entries',
        'scheduleFilterMode',
        'legacyPinsImported',
        'suggestionsMovedToTop',
        'sectionOrder',
        'hiddenSections',
        'collapsedSections',
      ],
      storage: window.localStorage,
    }).then(() => runInAction(() => this.reconcile()));
  }

  setVisible(key: string, visible: boolean) {
    this.entries = this.entries.map((entry) =>
      widgetEntryKey(entry) === key ? { ...entry, visible } : entry
    );
  }

  isSectionVisible(sectionId: HomeSectionId): boolean {
    if (sectionId === 'upcoming-meetings') {
      return this.entries.some(
        (entry) =>
          entry.kind === 'singleton' &&
          entry.type === 'upcoming-meetings' &&
          entry.visible
      );
    }
    return !this.hiddenSections.includes(sectionId);
  }

  setSectionVisible(sectionId: HomeSectionId, visible: boolean) {
    if (sectionId === 'upcoming-meetings') {
      this.setVisible('upcoming-meetings', visible);
      return;
    }
    const others = this.hiddenSections.filter((id) => id !== sectionId);
    this.hiddenSections = visible ? others : [...others, sectionId];
  }

  moveSection(sectionId: HomeSectionId, targetId: HomeSectionId) {
    const from = this.sectionOrder.indexOf(sectionId);
    const to = this.sectionOrder.indexOf(targetId);
    if (from === -1 || to === -1 || from === to) return;
    const order = [...this.sectionOrder];
    order.splice(from, 1);
    order.splice(to, 0, sectionId);
    this.sectionOrder = order;
  }

  isCollapsed(key: string): boolean {
    const entry = this.entries.find((e) => widgetEntryKey(e) === key);
    if (entry) return entry.collapsed;
    return this.collapsedSections.some((id) => id === key);
  }

  setCollapsed(key: string, collapsed: boolean) {
    if (this.entries.some((entry) => widgetEntryKey(entry) === key)) {
      this.entries = this.entries.map((entry) =>
        widgetEntryKey(entry) === key ? { ...entry, collapsed } : entry
      );
      return;
    }
    if (!isHomeSectionId(key)) return;
    const others = this.collapsedSections.filter((id) => id !== key);
    this.collapsedSections = collapsed ? [...others, key] : others;
  }

  setScheduleFilterMode(mode: ScheduleFilterMode) {
    this.scheduleFilterMode = mode;
  }

  addPinnedRoutine(routineId: number) {
    const existing = this.entries.find(
      (entry) =>
        entry.kind === 'pinned-routine' && entry.routineId === routineId
    );
    if (existing) {
      this.setVisible(widgetEntryKey(existing), true);
      return;
    }

    this.entries = [
      ...this.entries,
      {
        kind: 'pinned-routine',
        routineId,
        visible: true,
        collapsed: false,
        order: this.entries.length,
      },
    ];
    this.reconcile();
  }

  removeInstance(key: string) {
    this.entries = this.entries.filter(
      (entry) => entry.kind === 'singleton' || widgetEntryKey(entry) !== key
    );
    this.reconcile();
  }

  /**
   * Erases the pins of routines the server no longer has, now and whenever
   * the list changes. Only a list loaded from the server proves a routine is
   * gone; before that, a routine may not have arrived yet. Returns the disposer.
   */
  erasePinsOfDeletedRoutines(routines: RoutineList): IReactionDisposer {
    return reaction(
      () =>
        routines.hasLoadedFromServer
          ? this.entries.flatMap((entry) =>
              entry.kind === 'pinned-routine' &&
              !routines.getByReferenceId(entry.routineId)
                ? [entry.routineId]
                : []
            )
          : [],
      (deletedRoutineIds) => {
        if (deletedRoutineIds.length === 0) return;
        this.entries = this.entries.filter(
          (entry) =>
            entry.kind !== 'pinned-routine' ||
            !deletedRoutineIds.includes(entry.routineId)
        );
        this.reconcile();
      },
      { fireImmediately: true, equals: comparer.structural }
    );
  }

  importLegacyPins(entries: readonly WidgetEntry[]) {
    if (this.legacyPinsImported) return;

    const importedRoutineIds = new Set<number>();
    for (const entry of entries) {
      if (entry.kind !== 'pinned-routine') continue;
      if (importedRoutineIds.has(entry.routineId)) continue;
      importedRoutineIds.add(entry.routineId);
      this.addPinnedRoutine(entry.routineId);
      if (!entry.visible) {
        this.setVisible(`pinned-routine:${entry.routineId}`, false);
      }
    }
    this.legacyPinsImported = true;
  }

  reset() {
    this.entries = createDefaultEntries();
    this.scheduleFilterMode = 'all';
    this.sectionOrder = [...HOME_SECTION_IDS];
    this.suggestionsMovedToTop = true;
    this.hiddenSections = [];
    this.collapsedSections = [];
  }

  get orderedVisibleEntries(): WidgetEntry[] {
    return [...this.entries]
      .filter((entry) => entry.visible)
      .sort((a, b) => a.order - b.order);
  }

  private reconcile() {
    this.sectionOrder = reconcileSectionOrder(this.sectionOrder);
    if (!this.suggestionsMovedToTop) {
      if (
        comparer.structural(this.sectionOrder, OLD_DEFAULT_HOME_SECTION_IDS)
      ) {
        this.sectionOrder = [...HOME_SECTION_IDS];
      }
      this.suggestionsMovedToTop = true;
    }
    this.hiddenSections = Array.isArray(this.hiddenSections)
      ? [...new Set(this.hiddenSections.filter(isHomeSectionId))]
      : [];
    this.collapsedSections = Array.isArray(this.collapsedSections)
      ? [...new Set(this.collapsedSections.filter(isHomeSectionId))]
      : [];
    const persisted = parsePersistedEntries(this.entries);
    const singletonsByType = new Map(
      persisted.filter(isHomeSingleton).map((entry) => [entry.type, entry])
    );
    const singletons = createDefaultEntries().map((entry) =>
      entry.kind === 'singleton'
        ? (singletonsByType.get(entry.type) ?? entry)
        : entry
    );

    const pinnedRoutineIds = new Set<number>();
    const pins = persisted.filter(isPinnedRoutine).filter((entry) => {
      if (pinnedRoutineIds.has(entry.routineId)) return false;
      pinnedRoutineIds.add(entry.routineId);
      return true;
    });

    this.entries = [...singletons, ...pins].map((entry, order) => ({
      ...entry,
      order,
    }));
  }
}

export const homePreferencesStore = new HomePreferencesStore();

let initializationPromise: Promise<void> | null = null;

const hydrateIfNeeded = (store: object): Promise<void> =>
  isHydrated(store) ? Promise.resolve() : hydrateStore(store);

export const initializeHomePreferences = (): Promise<void> => {
  initializationPromise ??= Promise.all([
    hydrateIfNeeded(widgetsConfigStore),
    hydrateIfNeeded(homePreferencesStore),
  ]).then(() => {
    homePreferencesStore.importLegacyPins(widgetsConfigStore.entries);
  });
  return initializationPromise;
};
